import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Base: 44px min-height (iOS HIG touch target), rounded-xl, font-bold
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97] active:transition-none min-h-[44px]',
  {
    variants: {
      variant: {
        // PRIMARY — amber brand, dark text
        default:
          'bg-[var(--c-brand)] text-zinc-900 hover:bg-[var(--c-brand-h)] shadow-sm',
        // DESTRUCTIVE — red
        destructive:
          'bg-[var(--c-err)] text-white hover:bg-[var(--c-err)]/90',
        // OUTLINE — border only, transparent fill
        outline:
          'border border-[var(--c-border-str)] bg-transparent text-[var(--c-text-1)] hover:bg-[var(--c-bg-sunken)]',
        // SECONDARY — muted fill
        secondary:
          'bg-[var(--c-bg-sunken)] text-[var(--c-text-1)] hover:bg-[var(--c-border)]',
        // GHOST — no background
        ghost:
          'hover:bg-[var(--c-bg-sunken)] text-[var(--c-text-1)]',
        // GHOST-BRAND — ghost with amber text
        'ghost-brand':
          'hover:bg-[var(--c-brand-muted)] text-[var(--c-brand)]',
        // LINK
        link:
          'text-[var(--c-text-lnk)] underline-offset-4 hover:underline min-h-0',
        // SUCCESS
        success:
          'bg-[var(--c-ok)] text-white hover:bg-[var(--c-ok-h)]',
        // WARNING
        warning:
          'bg-[var(--c-warn)] text-white hover:opacity-90',
        // DARK (sidebar / game day contexts)
        dark:
          'bg-white/10 text-white hover:bg-white/20 border border-white/15',
      },
      size: {
        default: 'h-11 px-5 py-2',
        sm:      'h-9 rounded-lg px-3 text-xs min-h-[36px]',
        lg:      'h-12 rounded-xl px-8 text-base',
        icon:    'h-11 w-11',
        'icon-sm': 'h-9 w-9 rounded-lg min-h-[36px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Show a spinner and disable the button */
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          loading && 'opacity-75 cursor-not-allowed pointer-events-none',
        )}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        <span className={cn('contents', loading && 'animate-pulse')}>{children}</span>
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
